import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { compradorTrpc } from '../../lib/compradorTrpc';
import { AppShellComprador } from '../../components/comprador/AppShellComprador';
import { brl, dataCurta, useCompradorSessao, sairParaLogin } from './util';

interface Parcela {
  id: string;
  numero: number;
  valor: string;
  vencimento: string | null;
  statusPagamento: 'paga' | 'aberta' | 'atrasada';
  dataPagamento: string | null;
}

type Filtro = 'todas' | 'aberto' | 'pagas';

/**
 * Minhas parcelas (design aprovado): "Pagas até aqui X de Y" com anel de
 * percentual, filtros, seção A SEGUIR (em aberto) e JÁ CONQUISTADAS (pagas).
 * Parcela atrasada NUNCA aparece em vermelho ameaçador — o tom é neutro,
 * com o botão de 2ª via como caminho de resolução.
 */
export function ParcelasComprador() {
  const slug = useCompradorSessao();
  const [, navigate] = useLocation();
  const [corMarca, setCorMarca] = useState<string | null>(null);
  const [orgNome, setOrgNome] = useState('');
  const [parcelas, setParcelas] = useState<Parcela[]>([]);
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [carregando, setCarregando] = useState(true);
  const [pedindo, setPedindo] = useState<string | null>(null);
  const [confirmacao, setConfirmacao] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let vivo = true;
    Promise.all([
      compradorTrpc.auth.me.query({ orgSlug: slug }),
      compradorTrpc.app.parcelas.query({ orgSlug: slug }),
    ])
      .then(([m, p]) => {
        if (!vivo) return;
        setCorMarca(m.corMarca);
        setOrgNome(m.orgNome);
        setParcelas(p.parcelas as Parcela[]);
      })
      .catch(() => sairParaLogin(slug, navigate))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [slug, navigate]);

  async function pedirBoleto(parcelaId: string) {
    if (!slug) return;
    setPedindo(parcelaId);
    try {
      const r = await compradorTrpc.app.pedirBoleto.mutate({ orgSlug: slug, parcelaId });
      setConfirmacao(r.mensagem);
    } catch {
      setConfirmacao('Não foi possível registrar o pedido agora. Tente pela Conversa.');
    } finally {
      setPedindo(null);
    }
  }

  const pagas = parcelas.filter((p) => p.statusPagamento === 'paga');
  const abertas = parcelas
    .filter((p) => p.statusPagamento !== 'paga')
    .sort((a, b) => (a.vencimento ?? '9999').localeCompare(b.vencimento ?? '9999'));
  const pct = parcelas.length > 0 ? Math.round((pagas.length / parcelas.length) * 100) : 0;

  const mostraAbertas = filtro !== 'pagas';
  const mostraPagas = filtro !== 'aberto';

  const FILTROS: { valor: Filtro; rotulo: string }[] = [
    { valor: 'todas', rotulo: 'Todas' },
    { valor: 'aberto', rotulo: 'Em aberto' },
    { valor: 'pagas', rotulo: 'Pagas' },
  ];

  // Anel de progresso (SVG) do resumo.
  const R = 20;
  const CIRC = 2 * Math.PI * R;

  return (
    <AppShellComprador corMarca={corMarca} orgNome={orgNome}>
      <div data-testid="parcelas-comprador">
        <h1 className="font-serif text-[26px] font-medium tracking-tight text-ink">Minhas parcelas</h1>

        {carregando ? (
          <p className="py-16 text-center text-sm text-ink-3">Carregando…</p>
        ) : (
          <>
            <section className="card mt-4 flex items-center justify-between p-5" data-testid="parcelas-resumo">
              <div>
                <div className="text-xs text-ink-3">Pagas até aqui</div>
                <div className="num mt-1 font-serif text-[26px] font-medium text-ink">
                  {pagas.length} de {parcelas.length}
                </div>
              </div>
              <div className="relative h-14 w-14" aria-label={`${pct}% das parcelas pagas`}>
                <svg viewBox="0 0 48 48" className="h-14 w-14 -rotate-90">
                  <circle cx="24" cy="24" r={R} fill="none" stroke="var(--line)" strokeWidth="4.5" />
                  <circle
                    cx="24"
                    cy="24"
                    r={R}
                    fill="none"
                    stroke="var(--brand)"
                    strokeWidth="4.5"
                    strokeLinecap="round"
                    strokeDasharray={CIRC}
                    strokeDashoffset={CIRC * (1 - pct / 100)}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[12px] font-semibold text-ink">
                  {pct}%
                </span>
              </div>
            </section>

            <div className="mt-4 flex gap-2" role="tablist" aria-label="Filtrar parcelas">
              {FILTROS.map((f) => {
                const on = filtro === f.valor;
                return (
                  <button
                    key={f.valor}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setFiltro(f.valor)}
                    className="rounded-full border px-4 py-1.5 text-[13px] font-medium transition-colors"
                    style={
                      on
                        ? { background: 'var(--brand)', borderColor: 'var(--brand)', color: 'white' }
                        : { background: 'var(--surface)', borderColor: 'var(--line-2)', color: 'var(--ink-2)' }
                    }
                    data-testid={`filtro-${f.valor}`}
                  >
                    {f.rotulo}
                  </button>
                );
              })}
            </div>

            {confirmacao && (
              <div
                className="mt-4 rounded-2xl border p-3.5 text-[13px] leading-relaxed"
                style={{ background: 'var(--brand-soft)', borderColor: 'var(--line)', color: 'var(--ink-2)' }}
                data-testid="boleto-confirmacao"
              >
                {confirmacao}
              </div>
            )}

            {mostraAbertas && abertas.length > 0 && (
              <section className="mt-5">
                <div className="text-[11.5px] font-semibold uppercase tracking-[0.1em] text-ink-4">A seguir</div>
                <div className="mt-2 space-y-2.5">
                  {abertas.map((p, i) => (
                    <div
                      key={p.id}
                      className="card p-4"
                      style={i === 0 ? { borderColor: 'var(--brand)' } : undefined}
                      data-testid="parcela-aberta"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[15px] font-semibold text-ink">Parcela {p.numero}</div>
                          <div className="mt-0.5 text-xs text-ink-3">
                            {p.vencimento ? `vence ${dataCurta(p.vencimento)}` : 'vencimento a confirmar'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="num font-serif text-[18px] font-medium text-ink">{brl(Number(p.valor))}</div>
                          <span
                            className="mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold"
                            style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }}
                          >
                            Em aberto
                          </span>
                        </div>
                      </div>
                      {i === 0 && (
                        <button
                          type="button"
                          disabled={pedindo === p.id}
                          onClick={() => pedirBoleto(p.id)}
                          className="mt-3 w-full rounded-xl py-2.5 text-[13.5px] font-semibold text-white disabled:opacity-60"
                          style={{ background: 'var(--brand)' }}
                          data-testid="pedir-boleto"
                        >
                          {pedindo === p.id ? 'Pedindo…' : 'Pedir 2ª via do boleto'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {mostraPagas && pagas.length > 0 && (
              <section className="mt-6">
                <div className="text-[11.5px] font-semibold uppercase tracking-[0.1em] text-ink-4">Já conquistadas</div>
                <div className="mt-2 space-y-2">
                  {[...pagas].reverse().map((p) => (
                    <div key={p.id} className="card flex items-center gap-3 p-3.5" data-testid="parcela-paga">
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                        style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }}
                        aria-hidden="true"
                      >
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none">
                          <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-ink">Parcela {p.numero}</div>
                        <div className="text-xs text-ink-3">
                          {p.dataPagamento ? `paga ${dataCurta(p.dataPagamento)}` : 'paga'}
                        </div>
                      </div>
                      <div className="num font-serif text-[15px] font-medium text-ink">{brl(Number(p.valor))}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {parcelas.length === 0 && (
              <p className="py-16 text-center text-sm text-ink-3">
                Suas parcelas vão aparecer aqui assim que o contrato for confirmado.
              </p>
            )}
          </>
        )}
      </div>
    </AppShellComprador>
  );
}
